import { useMemo, useCallback } from "react";
import { useOptionalActivePageId, usePageProducts, useUpdatePageProducts, useAllProducts } from "../../../data";
import menuItems from "../../market-grid/menuItems";
import { captureEvent } from "../../../utils/o11y";
import { getSelectors } from "../../market-grid/context-menu/useGetMenuItemsFieldValue";
import type { TSelectorExchange, TSelectorMaturity } from "../../market-grid/selectorsData";
import { useGrid } from "../../market-grid/stores";

type FuturesFieldSubItem = {
  label: string;
  value: string;
  action: () => void;
  disabled: boolean;
};

type FuturesFieldMenuItem = {
  label: string;
  value: string;
  action: () => void;
  disabled: boolean;
  subItems?: FuturesFieldSubItem[];
};

export function useSetAllFuturesFieldsAction() {
  const pageId = useOptionalActivePageId();
  const pageProducts = usePageProducts(pageId);
  const allProducts = useAllProducts();
  const update = useUpdatePageProducts();
  const [gridApi] = useGrid();

  const futuresProductsWithSelectors = useMemo(() => {
    if (!pageId || !pageProducts.data || !allProducts.data) return [];

    return pageProducts.data
      .map((pageProduct) => {
        const product = allProducts.data?.[pageProduct.product_id ?? ""];
        if (!product) return null;

        if (product.artis_type !== "sourced" && product.artis_type !== "canned") return null;

        const exchange = product.packageByPackage?.sourceBySource?.exchange?.code;
        if (!exchange) return null;

        const availableSelectors = getSelectors({
          exchange: exchange as TSelectorExchange,
          maturity: product.maturity as TSelectorMaturity<TSelectorExchange>,
          productId: pageProduct.product_id ?? "",
        });

        if (availableSelectors.length === 0) return null;

        return {
          pageProduct,
          product,
          availableSelectors: availableSelectors.map((s) => s.value),
        };
      })
      .filter(Boolean);
  }, [pageId, pageProducts.data, allProducts.data]);

  const selectorsAvailableOnAll = useMemo(() => {
    if (futuresProductsWithSelectors.length === 0) return new Set<string>();

    const firstProductSelectors = new Set(futuresProductsWithSelectors[0].availableSelectors);

    for (let i = 1; i < futuresProductsWithSelectors.length; i++) {
      const productSelectors = new Set(futuresProductsWithSelectors[i].availableSelectors);
      for (const selector of firstProductSelectors) {
        if (!productSelectors.has(selector)) {
          firstProductSelectors.delete(selector);
        }
      }
    }

    return firstProductSelectors;
  }, [futuresProductsWithSelectors]);

  const currentFieldSelector = useMemo(
    () => futuresProductsWithSelectors[0]?.pageProduct.column_field_selector ?? "fv",
    [futuresProductsWithSelectors],
  );

  const setAllFuturesFields = useCallback(
    async (selector: string) => {
      const productsToUpdate = futuresProductsWithSelectors.filter((item) =>
        item.availableSelectors.includes(selector),
      );

      captureEvent("set_all_futures_fields", {
        selector,
        productCount: productsToUpdate.length,
      });

      const changes = productsToUpdate.map((item) => {
        return update.optimistic({
          id: item.pageProduct.id,
          column_field_selector: selector,
        });
      });

      await Promise.all(changes);

      if (gridApi && !gridApi.isDestroyed()) {
        gridApi.refreshHeader();
        gridApi.refreshCells({ force: true });
      }
    },
    [futuresProductsWithSelectors, update, gridApi],
  );

  const futuresFieldMenuItems: FuturesFieldMenuItem[] = useMemo(() => {
    const hasFutures = futuresProductsWithSelectors.length > 0;
    const items: FuturesFieldMenuItem[] = [];

    items.push({
      label: "FAIR VALUE",
      value: "fv",
      action: () => setAllFuturesFields("fv"),
      disabled: !hasFutures || !selectorsAvailableOnAll.has("fv"),
    });

    items.push({
      label: "CHGFV",
      value: "chgfv",
      action: () => setAllFuturesFields("chgfv"),
      disabled: !hasFutures || !selectorsAvailableOnAll.has("chgfv"),
    });

    for (const menuItem of menuItems) {
      const hasAnyAvailable = menuItem.subItems.some((subItem) => selectorsAvailableOnAll.has(subItem));

      const subItems: FuturesFieldSubItem[] = menuItem.subItems.map((subItem) => ({
        label: subItem.toUpperCase(),
        value: subItem,
        action: () => setAllFuturesFields(subItem),
        disabled: !hasFutures || !selectorsAvailableOnAll.has(subItem),
      }));

      items.push({
        label: menuItem.name,
        value: menuItem.subItems[0],
        action: () => setAllFuturesFields(menuItem.subItems[0]),
        disabled: !hasFutures || !hasAnyAvailable,
        subItems: subItems.length > 1 ? subItems : undefined,
      });
    }

    return items;
  }, [futuresProductsWithSelectors.length, selectorsAvailableOnAll, setAllFuturesFields]);

  return {
    futuresFieldMenuItems,
    currentFieldSelector,
    hasFuturesProducts: futuresProductsWithSelectors.length > 0,
  };
}
